<script setup lang="ts">
// @ts-nocheck
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { usePluginSession } from '../composables/usePluginSession'
import { usePostHog } from '../composables/usePostHog'
import { useConvexQuery, useConvexMutation } from 'convex-vue'
import { api } from '~/convex/_generated/api'
import type { Id } from '~/convex/_generated/dataModel'
import { useConvexFileUpload } from '@/composables/useFileUpload'
import { useNotifications } from '@/composables/useNotifications'
import { useMarkdown } from '@/composables/useMarkdown'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Paperclip, UserCircle2, Bot, X, Loader2, ArrowDown, Download, Image as ImageIcon, FileText, FileSpreadsheet, Copy, Check, ArrowRight, MessageSquare, Sparkles, RefreshCw, CornerDownLeft } from 'lucide-vue-next'
import QueueStatus from '../components/QueueStatus.vue'
import AiFeedbackButtons from '../components/AiFeedbackButtons.vue'
import hiyoAvatarSrc from '../assets/hiyo-avatar.png'

const session = usePluginSession()
const posthog = usePostHog()
const { playMessageSound } = useNotifications()
const { parseMarkdown } = useMarkdown()

const isReady = computed(() => {
  return (
    session.chatRoomId.value
    && session.userId.value
    && session.userName.value
    && session.clientId.value
    && session.productId.value
  )
})

// Queries
const { data: room } = useConvexQuery(
  api.chatRooms.getChatRoom,
  computed(() => {
    if (!session.userId.value || !session.chatRoomId.value) return undefined
    return {
      roomId: session.chatRoomId.value,
      userId: session.userId.value
    }
  })
)

// Lightweight room status for the widget — no agent access checks, owner-only
const { data: widgetRoomStatus } = useConvexQuery(
  api.chatRooms.getWidgetRoomStatus,
  computed(() => {
    if (!session.userId.value || !session.chatRoomId.value) return undefined
    return {
      roomId: session.chatRoomId.value,
      userId: session.userId.value
    }
  })
)

const { data: messages } = useConvexQuery(
  api.chatMessages.getMessages,
  computed(() => {
    if (!session.chatRoomId.value) return undefined
    return { roomId: session.chatRoomId.value }
  })
)

const { data: lastReadTimestamp } = useConvexQuery(
  api.chatMessages.getLastReadTimestamp,
  computed(() => {
    if (!session.chatRoomId.value || !session.userId.value) return undefined
    return {
      roomId: session.chatRoomId.value,
      userId: session.userId.value as Id<'users'>
    }
  })
)

// Queue position query (only when room is open and queued)
const { data: queuePosition } = useConvexQuery(
  api.queue.getQueuePosition,
  computed(() => {
    const roomId = session.chatRoomId.value
    const status = widgetRoomStatus.value

    if (!roomId || !status) return undefined
    if (status.status !== 'open') return undefined
    if (status.queueStatus !== 'queued') return undefined

    return { roomId }
  })
)

const { data: supportAvailability } = useConvexQuery(
  api.agentAvailability.getWidgetSupportAvailability,
  computed(() => {
    if (!session.userId.value || !session.chatRoomId.value) return undefined
    return {
      roomId: session.chatRoomId.value,
      userId: session.userId.value as Id<'users'>
    }
  })
)

// Query product info for humanRequestEnabled flag
const { data: product } = useConvexQuery(
  api.products.get,
  computed(() => {
    if (!session.productId.value || !session.userId.value) return undefined
    return { id: session.productId.value, userId: session.userId.value }
  })
)

// Mutations
const { mutate: sendMessageMutation } = useConvexMutation(api.chatMessages.sendMessage)
const { mutate: markAsReadMutation } = useConvexMutation(api.chatMessages.markMessagesAsRead)
const { mutate: cancelAIProcessingMutation } = useConvexMutation(api.chatRooms.cancelAIProcessing)
const { mutate: requestHumanAssistanceMutation, isPending: isRequestingHuman } = useConvexMutation(api.chatRooms.requestHumanAssistance)
const { mutate: resetConversationMutation, isPending: isResettingConversation } = useConvexMutation(api.chatRooms.resetConversation)

// State
const messageText = ref('')
const fileInput = ref<HTMLInputElement>()
const uploadedFiles = ref<File[]>([])
const isSubmitting = ref(false)
const isNearBottom = ref(true)
const showScrollButton = ref(false)
const hasInitiallyScrolled = ref(false)
const messagesContainerRef = ref<HTMLElement>()

// Maintenance mode - temporarily disable chat input
const isMaintenanceMode = ref(false)

// Local flag to immediately hide the human-request button after clicking
const humanRequested = ref(false)
const hasObservedActiveQueueState = ref(false)

// AI typing indicator state - uses real progress from backend
const aiTypingSteps = [
  { step: 1, message: 'Understanding your question...' },
  { step: 2, message: 'Searching knowledge base...' },
  { step: 3, message: 'Analyzing relevant information...' },
  { step: 4, message: 'Formulating response...' },
  { step: 5, message: 'Finalizing answer...' }
]

// Real progress from backend (room.aiProcessingStep)
const isWaitingForAI = computed(() => (room.value?.aiProcessingStep ?? 0) > 0)
const currentAITypingStep = computed(() => {
  const step = room.value?.aiProcessingStep ?? 0
  if (step === 0) return aiTypingSteps[0]
  return aiTypingSteps[step - 1] || aiTypingSteps[aiTypingSteps.length - 1]
})

const isRoomInHumanQueue = computed(() => {
  const queueStatus = widgetRoomStatus.value?.queueStatus
  return queueStatus === 'queued' || queueStatus === 'claimed' || queueStatus === 'assigned'
})

const hasActiveHumanAgent = computed(() => (room.value?.currentAgentIds?.length ?? 0) > 0)
const hasOnlineSupportAgents = computed(() => supportAvailability.value?.hasOnlineAgents === true)

const isHumanRequestLocked = computed(() => {
  return humanRequested.value || isRoomInHumanQueue.value || hasActiveHumanAgent.value
})

watch(
  () => ({
    status: widgetRoomStatus.value?.status,
    queueStatus: widgetRoomStatus.value?.queueStatus
  }),
  ({ status, queueStatus }) => {
    if (status === 'closed') {
      humanRequested.value = false
      hasObservedActiveQueueState.value = false
      return
    }

    if (queueStatus === 'queued' || queueStatus === 'claimed' || queueStatus === 'assigned') {
      humanRequested.value = true
      hasObservedActiveQueueState.value = true
      return
    }

    if (queueStatus === 'not_queued' && hasObservedActiveQueueState.value) {
      humanRequested.value = false
      hasObservedActiveQueueState.value = false
    }
  }
)

// Watch for AI processing to scroll to bottom when it starts
watch(isWaitingForAI, (waiting) => {
  if (waiting) {
    nextTick(() => {
      performScrollToBottom()
    })
  }
})

// Copy message functionality
const copiedMessageId = ref<string | null>(null)
const hasEnteredConversationMode = ref(false)
const canRequestHuman = computed(() => {
  return product.value?.humanRequestEnabled !== false && hasOnlineSupportAgents.value
})
const showCallHumanAction = computed(() => {
  if (product.value?.humanRequestEnabled === false) return false
  if (isHumanRequestLocked.value) return false
  if (isRequestingHuman.value) return true

  // Product access alone is not enough. If the client's team coverage does not
  // expose an eligible support path, do not render a request-looking action.
  // Older deployed backends may not return an unavailableReason yet, so the
  // absence of hasOnlineAgents=true is enough to hide the affordance.
  return canRequestHuman.value
})

const hasLoadedMessages = computed(() => messages.value !== undefined)

const showSupportHome = computed(() => {
  if (!hasLoadedMessages.value) return false

  return widgetRoomStatus.value?.status === 'closed'
    && displayMessages.value.length === 0
    && !hasEnteredConversationMode.value
    && !isWaitingForAI.value
    && !isMaintenanceMode.value
})

const showQueueStatus = computed(() => {
  const queueStatus = widgetRoomStatus.value?.queueStatus

  if (isRequestingHuman.value) return true
  if (queueStatus === 'queued') return true
  if (queueStatus === 'claimed' || queueStatus === 'assigned') return false

  // Keep the queue card visible right after a human request while the room
  // status and queue-position queries catch up.
  return humanRequested.value
})

watch(showQueueStatus, (visible, wasVisible) => {
  if (!visible || wasVisible) return

  nextTick(() => {
    const queueStatusCard = document.querySelector('[data-testid="queue-status"]') as HTMLElement | null
    queueStatusCard?.scrollIntoView({
      block: 'start',
      behavior: 'smooth'
    })
  })
})

function activateConversationMode() {
  hasEnteredConversationMode.value = true
}

function focusComposer() {
  nextTick(() => {
    const textarea = document.querySelector('.abba-chat-input') as HTMLTextAreaElement | null
    if (textarea) {
      textarea.focus()
      textarea.setSelectionRange(textarea.value.length, textarea.value.length)
    }
  })
}

function clearComposer() {
  messageText.value = ''
  uploadedFiles.value = []
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

async function startNewTopic() {
  if (!session.chatRoomId.value || isResettingConversation.value) return

  clearComposer()
  activateConversationMode()

  try {
    await resetConversationMutation({
      roomId: session.chatRoomId.value
    })
    humanRequested.value = false
    posthog.trackConversationReset({
      clientId: session.clientId.value,
      productId: session.productId.value,
      roomId: session.chatRoomId.value
    })
    await nextTick()
    performScrollToBottom()
    focusComposer()
  } catch (error) {
    console.error('Failed to start new topic:', error)
  }
}

async function handleAssistanceClick() {
  if (!humanRequested.value && widgetRoomStatus.value?.status === 'closed' && canRequestHuman.value) {
    activateConversationMode()
    await requestHuman()
    return
  }

  activateConversationMode()
  focusComposer()
}

async function copyMessage(messageId: string, content: string) {
  try {
    await navigator.clipboard.writeText(content)
    copiedMessageId.value = messageId
    setTimeout(() => {
      copiedMessageId.value = null
    }, 2000)
  } catch (error) {
    console.error('Failed to copy message:', error)
  }
}
const optimisticMessages = ref<Array<{
  id: string
  content: string
  files: Array<{
    file: File
    progress: number
  }>
  isUploading: boolean
}>>([])

// File upload
const { uploadMultipleFiles, cancelUpload, isUploading } = useConvexFileUpload()

// Calculate unread messages
const unreadMessages = computed(() => {
  if (!messages.value || !lastReadTimestamp.value) return messages.value ?? []
  return messages.value.filter(m => m.timestamp > lastReadTimestamp.value)
})

const unreadCount = computed(() => unreadMessages.value.length)

// Find the index of the first unread message
const firstUnreadIndex = computed(() => {
  if (!messages.value || unreadMessages.value.length === 0) return -1
  const firstUnread = unreadMessages.value[0]
  return messages.value.findIndex(m => m._id === firstUnread._id)
})

// Merge real messages with optimistic uploading messages
const displayMessages = computed(() => {
  const realMessages = messages.value || []
  const optimistic = optimisticMessages.value.map(opt => ({
    _id: opt.id,
    roomId: session.chatRoomId.value!,
    senderId: session.userId.value!,
    senderType: 'client' as const,
    senderName: session.userName.value || 'You',
    content: opt.content,
    fileMetadata: opt.files.map(f => ({
      fileId: '' as any,
      fileName: f.file.name,
      fileType: f.file.type,
      fileSizeBytes: f.file.size,
      fileUrl: null,
      isUploading: true,
      uploadProgress: f.progress
    })),
    timestamp: Date.now(),
    isOptimistic: true,
    optimisticId: opt.id
  }))
  return [...realMessages, ...optimistic]
})

const visibleMessages = computed(() => displayMessages.value)

// Watch messages for file attachments debugging
watch(messages, (msgs) => {
  if (msgs) {
    const messagesWithFiles = msgs.filter(m => m.fileMetadata && m.fileMetadata.length > 0)
    if (messagesWithFiles.length > 0) {
      console.log('[ChatTab] Messages with files:', messagesWithFiles.map(m => ({
        id: m._id,
        files: m.fileMetadata?.map(f => ({
          fileName: f.fileName,
          fileId: f.fileId,
          hasUrl: !!f.fileUrl,
          fileUrl: f.fileUrl
        }))
      })))
    }
  }
})

// Watch for initial load - scroll to bottom when widget opens
watch([isReady, messages], async ([ready, msgs]) => {
  if (ready && msgs && msgs.length > 0 && !hasInitiallyScrolled.value) {
    hasInitiallyScrolled.value = true
    performScrollToBottom()
  }
}, { immediate: true })

// Watch messages for new message events
watch(messages, async (newMessages, oldMessages) => {
  if (!oldMessages || oldMessages.length === 0) {
    console.log('[ChatTab] Initial messages load, skipping sound')
    return
  }

  await nextTick()

  // Check if there's a new message by comparing the last message ID
  // (length comparison doesn't work when we hit the 100 message limit)
  const hasNewMessage = newMessages && oldMessages
    && newMessages.length > 0 && oldMessages.length > 0
    && newMessages[newMessages.length - 1]._id !== oldMessages[oldMessages.length - 1]._id

  if (hasNewMessage) {
    const latestMessage = newMessages[newMessages.length - 1]
    console.log('[ChatTab] New message detected:', {
      sender: latestMessage.senderName,
      type: latestMessage.senderType,
      content: latestMessage.content.substring(0, 50),
      isCurrentUser: isCurrentUser(latestMessage)
    })

    // Play sound if the message is not from the current user
    if (!isCurrentUser(latestMessage)) {
      console.log('[ChatTab] Playing sound for message from:', latestMessage.senderName)
      playMessageSound()
    } else {
      console.log('[ChatTab] Skipping sound - message from current user')
    }
  }

  // If user is near bottom or this is right after sending, auto-scroll
  if (isNearBottom.value) {
    performScrollToBottom()
    // Auto-mark new messages as read when at bottom
    if (hasNewMessage && unreadCount.value > 0) {
      setTimeout(() => markVisibleMessagesAsRead(), 150)
    }
  } else if (hasNewMessage) {
    // New message arrived while user scrolled up - show button
    showScrollButton.value = true
  }
}, { deep: true })

// Template population event handler
const handlePopulateInput = (event: CustomEvent) => {
  if (event.detail?.template) {
    activateConversationMode()
    messageText.value = event.detail.template
    focusComposer()
  }
}

// Setup scroll listener on mount
onMounted(() => {
  nextTick(() => {
    setupScrollListener()
  })

  // Listen for template population events from parent
  window.addEventListener('populate-chat-input', handlePopulateInput as EventListener)
})

onUnmounted(() => {
  const viewport = getScrollViewport()
  if (viewport) {
    viewport.removeEventListener('scroll', handleScroll)
  }

  // Remove template population event listener
  window.removeEventListener('populate-chat-input', handlePopulateInput as EventListener)
})

function getScrollViewport(): HTMLElement | null {
  if (messagesContainerRef.value) {
    return messagesContainerRef.value
  }
  return null
}

function setupScrollListener() {
  const viewport = getScrollViewport()
  if (viewport) {
    viewport.addEventListener('scroll', handleScroll)
  }
}

function handleScroll() {
  const viewport = getScrollViewport()
  if (!viewport) return

  const { scrollTop, scrollHeight, clientHeight } = viewport
  const distanceFromBottom = scrollHeight - scrollTop - clientHeight
  const wasNearBottom = isNearBottom.value

  isNearBottom.value = distanceFromBottom < 100
  showScrollButton.value = !isNearBottom.value && unreadCount.value > 0

  // Only mark as read when transitioning to near bottom (not continuously while at bottom)
  if (isNearBottom.value && !wasNearBottom && unreadCount.value > 0) {
    markVisibleMessagesAsRead()
  }
}

function performScrollToBottom() {
  const attempts = [0, 50, 100, 150, 250, 400, 600]

  attempts.forEach((delay) => {
    setTimeout(() => {
      const viewport = getScrollViewport()
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight
      }
    }, delay)
  })
}

function scrollToBottomAndMarkRead() {
  performScrollToBottom()
  showScrollButton.value = false
  setTimeout(() => markVisibleMessagesAsRead(), 100)
}

async function markVisibleMessagesAsRead() {
  if (!messages.value || messages.value.length === 0) return
  if (!session.userId.value || !session.chatRoomId.value) return

  // Get the latest message timestamp
  const latestMessage = messages.value[messages.value.length - 1]
  if (!latestMessage) return

  // Only mark as read if there are actually unread messages
  if (unreadCount.value > 0) {
    try {
      await markAsReadMutation({
        roomId: session.chatRoomId.value,
        userId: session.userId.value as Id<'users'>,
        upToTimestamp: latestMessage.timestamp
      })
    } catch (error) {
      console.error('Failed to mark messages as read:', error)
    }
  }
}

async function stopAIProcessing() {
  if (!session.chatRoomId.value) return

  try {
    await cancelAIProcessingMutation({
      roomId: session.chatRoomId.value
    })
  } catch (error) {
    console.error('Failed to stop AI processing:', error)
  }
}

async function requestHuman() {
  if (!session.chatRoomId.value || !session.userId.value) return

  try {
    humanRequested.value = true
    await requestHumanAssistanceMutation({
      roomId: session.chatRoomId.value,
      requestedBy: session.userId.value
    })
  } catch (error) {
    humanRequested.value = false
    console.error('Failed to request human assistance:', error)
  }
}

function handleKeyDown(event: KeyboardEvent) {
  // If AI is processing, don't allow any submission
  if (isWaitingForAI.value) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
    }
    return
  }

  // Handle Enter key
  if (event.key === 'Enter') {
    if (event.shiftKey) {
      // Shift+Enter: allow default behavior (new line)
      return
    } else {
      // Enter alone: send message
      event.preventDefault()
      sendMessage()
    }
  }
}

async function sendMessage() {
  if (!messageText.value.trim() && uploadedFiles.value.length === 0) return
  if (!session.chatRoomId.value || !session.userId.value || !session.userName.value) return
  activateConversationMode()

  const messageCopy = messageText.value.trim() || '📎 Sent files'
  const filesCopy = [...uploadedFiles.value]
  const hasFiles = filesCopy.length > 0

  // Clear input immediately
  messageText.value = ''
  uploadedFiles.value = []

  // If there are files, show optimistic message immediately
  if (hasFiles) {
    const optimisticId = `optimistic-${Date.now()}`
    optimisticMessages.value.push({
      id: optimisticId,
      content: messageCopy,
      files: filesCopy.map(f => ({ file: f, progress: 0 })),
      isUploading: true
    })

    // Force scroll to bottom to show the optimistic message
    isNearBottom.value = true
    await nextTick()
    performScrollToBottom()

    // Upload files in the background with progress tracking
    try {
      const uploadedMetadata = await uploadMultipleFiles(
        filesCopy,
        (fileName, progressData) => {
          // Update progress for this specific file in the optimistic message
          const optMsg = optimisticMessages.value.find(m => m.id === optimisticId)
          if (optMsg) {
            const fileEntry = optMsg.files.find(f => f.file.name === fileName)
            if (fileEntry) {
              fileEntry.progress = progressData.progress
            }
          }
        }
      )

      if (uploadedMetadata.length > 0) {
        const fileIds = uploadedMetadata.map(m => m.fileId)
        const fileMetadata = uploadedMetadata

        // Send the actual message
        await sendMessageMutation({
          roomId: session.chatRoomId.value,
          senderId: session.userId.value,
          senderType: 'client',
          senderName: session.userName.value,
          content: messageCopy,
          fileIds,
          fileMetadata
        })
      }

      // Remove optimistic message
      optimisticMessages.value = optimisticMessages.value.filter(m => m.id !== optimisticId)

      // Force scroll to bottom after real message appears
      isNearBottom.value = true
      await nextTick()
      performScrollToBottom()
    } catch (error) {
      console.error('Failed to upload files or send message:', error)
      // Remove optimistic message on error
      optimisticMessages.value = optimisticMessages.value.filter(m => m.id !== optimisticId)
      alert('Failed to send message with files. Please try again.')
    }
  } else {
    // No files, send message immediately
    isSubmitting.value = true

    try {
      await sendMessageMutation({
        roomId: session.chatRoomId.value,
        senderId: session.userId.value,
        senderType: 'client',
        senderName: session.userName.value,
        content: messageCopy
      })

      // Track message sent
      posthog.trackMessageSent({
        clientId: session.clientId.value,
        productId: session.productId.value,
        hasFiles: hasFiles,
        messageLength: messageCopy.length
      })

      // Force scroll to bottom after sending - user expects to see their message
      isNearBottom.value = true
      await nextTick()
      performScrollToBottom()
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      isSubmitting.value = false
    }
  }
}

function handleCancelUpload(optimisticId: string, fileName: string) {
  // Cancel the upload
  cancelUpload(fileName)

  // Remove the optimistic message
  optimisticMessages.value = optimisticMessages.value.filter(m => m.id !== optimisticId)
}

function handleFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.files) {
    activateConversationMode()
    uploadedFiles.value = [...uploadedFiles.value, ...Array.from(target.files)]
  }
}

function removeFile(index: number) {
  uploadedFiles.value.splice(index, 1)
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatTime(timestamp: number) {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(new Date(timestamp))
}

function getSenderInitials(senderName: string) {
  return senderName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

// Check if a message is from the current user
function isCurrentUser(message: any) {
  return message.senderId === session.userId.value
}

function getDisplaySenderName(message: any) {
  if (isCurrentUser(message)) {
    return 'You'
  }

  if (message.senderType === 'ai') {
    return 'Hiyo'
  }

  if (message.senderType === 'agent') {
    return 'YAHSHUA Consultant'
  }

  return message.senderName
}

function getDisplaySenderInitials(message: any) {
  return isCurrentUser(message) ? 'Y' : getSenderInitials(message.senderName)
}

// File handling functions
function isImageFile(fileName: string): boolean {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp']
  return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext))
}

function getFileIcon(fileName: string) {
  const lowerName = fileName.toLowerCase()
  if (isImageFile(fileName)) return ImageIcon
  if (lowerName.endsWith('.pdf')) return FileText
  if (lowerName.endsWith('.xls') || lowerName.endsWith('.xlsx') || lowerName.endsWith('.csv')) return FileSpreadsheet
  if (lowerName.endsWith('.doc') || lowerName.endsWith('.docx')) return FileText
  return Paperclip
}

function getFileExtension(fileName: string) {
  const extension = fileName.split('.').pop()
  return extension && extension !== fileName ? extension.toUpperCase() : 'FILE'
}

function getFileTypeLabel(fileName: string) {
  const lowerName = fileName.toLowerCase()
  if (isImageFile(fileName)) return 'Image'
  if (lowerName.endsWith('.pdf')) return 'PDF document'
  if (lowerName.endsWith('.xls') || lowerName.endsWith('.xlsx')) return 'Spreadsheet'
  if (lowerName.endsWith('.csv')) return 'CSV file'
  if (lowerName.endsWith('.doc') || lowerName.endsWith('.docx')) return 'Word document'
  if (lowerName.endsWith('.mp4') || lowerName.endsWith('.mov') || lowerName.endsWith('.webm')) return 'Video'
  return 'Attachment'
}

function handleFileClick(fileUrl: string | null | undefined, fileName: string) {
  console.log('[ChatTab] handleFileClick called:', { fileUrl, fileName })

  if (!fileUrl) {
    console.error('[ChatTab] File URL not available for file:', fileName)
    alert('Unable to load file. Please try refreshing the page.')
    return
  }

  window.open(fileUrl, '_blank')
}

</script>

<template>
  <div class="abba-chat-container">
    <!-- Loading State -->
    <div
      v-if="!isReady"
      class="flex items-center justify-center h-full"
    >
      <div class="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 class="w-8 h-8 animate-spin" />
        <p class="text-sm">
          Loading chat...
        </p>
      </div>
    </div>

    <!-- Chat Interface -->
    <template v-else>
      <!-- Maintenance Mode Full Screen Message -->
      <div
        v-if="isMaintenanceMode"
        class="flex items-center justify-center h-full p-8"
      >
        <div class="text-center space-y-4 max-w-md">
          <div class="bg-amber-100 dark:bg-amber-900/30 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-8 h-8 text-amber-600 dark:text-amber-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-foreground">
            Chat Temporarily Unavailable
          </h3>
          <p class="text-sm text-muted-foreground">
            We're making some adjustments to our smart chat. We'll enable it again soon!
          </p>
          <p class="text-xs text-muted-foreground">
            Thank you for your patience.
          </p>
        </div>
      </div>

      <!-- Messages Area -->
      <div
        v-else
        class="abba-messages-area"
      >
        <div
          ref="messagesContainerRef"
          class="abba-messages-scroll scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
        >
          <div
            v-if="showSupportHome"
            class="abba-support-home"
          >
            <div class="abba-support-home-copy">
              <MessageSquare class="abba-empty-state-icon" />
              <div>
                <div class="abba-empty-state-title">
                  Hi there — what can we help with?
                </div>
                <div class="abba-empty-state-text">
                  Read the guide, start a new topic, or Call for a human if they are available. Replies usually arrive in a minute or two.
                </div>
              </div>
            </div>
          </div>

          <!-- Empty state when no messages -->
          <div
            v-else-if="hasLoadedMessages && (!visibleMessages || visibleMessages.length === 0)"
            class="abba-empty-state"
          >
            <MessageSquare class="abba-empty-state-icon" />
            <div class="abba-empty-state-title">
              Hi there — what can we help with?
            </div>
            <div class="abba-empty-state-text">
              Read the guide, start a new topic, or Call for a human if they are available. Replies usually arrive in a minute or two.
            </div>
          </div>

          <div
            v-else
            class="space-y-4"
          >
            <div
              v-for="(message, index) in visibleMessages"
              :key="message._id"
            >
              <!-- New Messages Divider -->
              <div
                v-if="index === firstUnreadIndex && firstUnreadIndex > 0"
                class="flex items-center gap-3 my-6"
              >
                <div class="flex-1 h-px bg-primary/30" />
                <Badge
                  variant="default"
                  class="px-3 py-1"
                >
                  New messages
                </Badge>
                <div class="flex-1 h-px bg-primary/30" />
              </div>

              <div
                class="flex gap-3 group/message"
                :class="isCurrentUser(message) ? 'justify-end' : 'justify-start'"
              >
                <Avatar
                  v-if="!isCurrentUser(message)"
                  class="w-8 h-8"
                >
                  <img
                    v-if="message.senderType === 'ai'"
                    :src="hiyoAvatarSrc"
                    alt="Hiyo avatar"
                    class="h-full w-full object-cover"
                  />
                  <AvatarFallback v-if="message.senderType !== 'ai'">
                    <Bot
                      v-if="message.senderType === 'ai'"
                      class="w-4 h-4"
                    />
                    <UserCircle2
                      v-else-if="message.senderType === 'agent'"
                      class="w-4 h-4"
                    />
                    <span v-else>{{ getSenderInitials(message.senderName) }}</span>
                  </AvatarFallback>
                </Avatar>

                <div
                  class="max-w-[70%] space-y-1"
                  :class="isCurrentUser(message) ? 'items-end' : 'items-start'"
                >
                  <div class="flex items-center gap-2 text-xs text-muted-foreground">
                    <span class="font-medium">{{ getDisplaySenderName(message) }}</span>
                    <span>{{ formatTime(message.timestamp) }}</span>
                    <!-- Copy button - appears on hover -->
                    <button
                      v-if="!message.isOptimistic"
                      class="opacity-0 group-hover/message:opacity-70 hover:!opacity-100 transition-opacity p-1 rounded hover:bg-muted"
                      :title="copiedMessageId === message._id ? 'Copied!' : 'Copy message'"
                      @click="copyMessage(message._id, message.content)"
                    >
                      <Check
                        v-if="copiedMessageId === message._id"
                        class="w-3 h-3 text-green-600"
                      />
                      <Copy
                        v-else
                        class="w-3 h-3"
                      />
                    </button>
                  </div>

                  <div
                    :class="[
                      'rounded-lg px-4 py-2 break-words',
                      message.senderType === 'ai'
                        ? 'abba-message-bubble-ai'
                        : isCurrentUser(message)
                          ? 'abba-message-bubble-user'
                          : 'abba-message-bubble-agent'
                    ]"
                  >
                    <div
                      class="markdown-content"
                      v-html="parseMarkdown(message.content)"
                    />

                    <div
                      v-if="message.fileMetadata && message.fileMetadata.length > 0"
                      class="mt-2 space-y-2"
                    >
                      <template
                        v-for="file in message.fileMetadata"
                        :key="file.fileId || file.fileName"
                      >
                        <!-- Uploading indicator with progress -->
                        <div
                          v-if="file.isUploading"
                          class="space-y-1"
                        >
                          <div class="flex items-center justify-between gap-2 text-sm">
                            <div class="flex items-center gap-2 flex-1">
                              <Loader2 class="w-3 h-3 animate-spin flex-shrink-0" />
                              <span class="truncate">{{ file.fileName }}</span>
                              <span class="text-xs opacity-70 flex-shrink-0">({{ formatFileSize(file.fileSizeBytes) }})</span>
                            </div>
                            <button
                              class="text-xs opacity-70 hover:opacity-100 transition-opacity flex-shrink-0"
                              title="Cancel upload"
                              @click="handleCancelUpload(message.optimisticId, file.fileName)"
                            >
                              <X class="w-3 h-3" />
                            </button>
                          </div>
                          <!-- Progress bar -->
                          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                            <div
                              class="bg-primary h-1.5 rounded-full transition-all duration-300"
                              :style="{ width: `${file.uploadProgress || 0}%` }"
                            />
                          </div>
                          <div class="text-xs opacity-70">
                            {{ file.uploadProgress || 0 }}% uploaded
                          </div>
                        </div>

                        <!-- All files render as a downloadable attachment card (no image previews) -->
                        <button
                          v-else
                          type="button"
                          data-variant="attachment"
                          class="group flex w-full max-w-[320px] items-center gap-3 rounded-2xl border border-slate-200/90 bg-white px-3 py-3 text-left text-slate-900 shadow-[0_8px_24px_-18px_rgba(15,23,42,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_16px_30px_-20px_rgba(15,23,42,0.7)] active:translate-y-0"
                          :aria-label="`Open attachment ${file.fileName}`"
                          @click="handleFileClick(file.fileUrl, file.fileName)"
                        >
                          <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200">
                            <component
                              :is="getFileIcon(file.fileName)"
                              class="h-5 w-5"
                            />
                          </span>
                          <span class="min-w-0 flex-1">
                            <span class="block truncate text-sm font-semibold leading-5 text-slate-950">
                              {{ file.fileName }}
                            </span>
                            <span class="mt-0.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-slate-500">
                              <span>{{ getFileExtension(file.fileName) }}</span>
                              <span class="h-1 w-1 rounded-full bg-slate-300" />
                              <span class="normal-case tracking-normal">{{ formatFileSize(file.fileSizeBytes) }}</span>
                            </span>
                            <span class="sr-only">{{ getFileTypeLabel(file.fileName) }}</span>
                          </span>
                          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500 transition-colors group-hover:border-slate-300 group-hover:bg-slate-900 group-hover:text-white">
                            <Download class="h-4 w-4" />
                          </span>
                        </button>
                      </template>
                    </div>
                  </div>

                  <!-- AI Disclaimer -->
                  <div
                    v-if="message.senderType === 'ai'"
                    class="text-xs text-muted-foreground mt-1 italic"
                  >
                    Please review suggestions before applying. If something doesn't work, revert your changes.
                  </div>

                  <!-- AI Feedback Buttons -->
                  <AiFeedbackButtons
                    v-if="message.senderType === 'ai' && !message.isOptimistic && session.userId.value"
                    :message-id="message._id"
                    :user-id="session.userId.value"
                  />

                  <Badge
                    v-if="message.isAgentTakeover"
                    variant="outline"
                    class="text-xs"
                  >
                    Agent joined
                  </Badge>
                </div>

                <Avatar
                  v-if="isCurrentUser(message)"
                  class="w-8 h-8"
                >
                  <AvatarFallback>
                    {{ getDisplaySenderInitials(message) }}
                  </AvatarFallback>
                </Avatar>
              </div>
            </div>

            <!-- AI Typing Indicator with Step Progress -->
            <div
              v-if="isWaitingForAI"
              class="flex gap-3 justify-start"
            >
              <Avatar class="w-8 h-8">
                <img
                  :src="hiyoAvatarSrc"
                  alt="Hiyo avatar"
                  class="h-full w-full object-cover"
                />
              </Avatar>
              <div class="max-w-[80%] space-y-1">
                <div class="flex items-center gap-2 text-xs text-muted-foreground">
                  <span class="font-medium">Hiyo</span>
                </div>
                <div class="abba-typing-bubble">
                  <!-- Step progress indicator -->
                  <div class="flex items-center gap-2 mb-2">
                    <div class="flex gap-1">
                      <span
                        v-for="step in aiTypingSteps"
                        :key="step.step"
                        class="w-2 h-2 rounded-full transition-all duration-300"
                        :class="step.step <= currentAITypingStep.step ? 'bg-white' : 'bg-white/30'"
                      />
                    </div>
                    <span class="text-white/70 text-xs">Step {{ currentAITypingStep.step }} of {{ aiTypingSteps.length }}</span>
                  </div>
                  <!-- Current step message -->
                  <div class="flex items-center gap-2">
                    <Loader2 class="w-4 h-4 text-white animate-spin" />
                    <span class="text-white text-sm transition-all duration-300">{{ currentAITypingStep.message }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Floating Scroll to Bottom Button with Unread Count -->
        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          leave-active-class="transition-all duration-200 ease-in"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-2"
        >
          <div
            v-if="showScrollButton && unreadCount > 0"
            class="absolute bottom-4 right-4 z-10"
          >
            <Button
              size="icon"
              class="h-12 w-12 rounded-full shadow-lg relative"
              @click="scrollToBottomAndMarkRead"
            >
              <ArrowDown class="w-5 h-5" />
              <Badge
                variant="destructive"
                class="absolute -top-1 -right-1 h-6 w-6 rounded-full p-0 flex items-center justify-center text-xs"
              >
                {{ unreadCount > 99 ? '99+' : unreadCount }}
              </Badge>
            </Button>
          </div>
        </Transition>
      </div>

      <!-- Message Input -->
      <div
        v-if="!isMaintenanceMode"
        class="abba-input-area space-y-2"
      >
        <div
          v-if="uploadedFiles.length > 0"
          class="flex gap-2 flex-wrap"
        >
          <Badge
            v-for="(file, i) in uploadedFiles"
            :key="i"
            variant="secondary"
            class="pr-1"
          >
            {{ file.name }} ({{ formatFileSize(file.size) }})
            <Button
              variant="ghost"
              size="icon"
              class="h-4 w-4 ml-1"
              @click="removeFile(i)"
            >
              <X class="w-3 h-3" />
            </Button>
          </Badge>
        </div>

        <div class="flex gap-2">
          <input
            ref="fileInput"
            type="file"
            multiple
            accept="image/*,.pdf,.csv,.xls,.xlsx,.doc,.docx,video/*"
            class="hidden"
            @change="handleFileSelect"
          >

          <Textarea
            v-model="messageText"
            placeholder="Write a message..."
            class="flex-1 min-h-[44px] max-h-[140px] abba-chat-input abba-composer-textarea"
            :disabled="isWaitingForAI || isMaintenanceMode"
            @focus="activateConversationMode"
            @keydown="handleKeyDown"
          />

          <Button
            type="button"
            :disabled="isMaintenanceMode || (!isWaitingForAI && (isSubmitting || isUploading || (!messageText.trim() && uploadedFiles.length === 0)))"
            :variant="isWaitingForAI ? 'destructive' : 'default'"
            class="abba-send-button"
            @click="isWaitingForAI ? stopAIProcessing() : sendMessage()"
          >
            <X
              v-if="isWaitingForAI"
              class="w-4 h-4"
            />
            <Loader2
              v-else-if="isSubmitting || isUploading"
              class="w-4 h-4 animate-spin"
            />
            <ArrowRight
              v-else
              class="w-4 h-4"
            />
          </Button>
        </div>

        <div class="abba-footer-actions">
          <Button
            v-if="showCallHumanAction"
            type="button"
            variant="ghost"
            class="abba-footer-action"
            :disabled="!canRequestHuman || isHumanRequestLocked || isRequestingHuman || isWaitingForAI"
            @click="handleAssistanceClick"
          >
            <Sparkles class="w-4 h-4" />
            Call Human
          </Button>
          <Button
            type="button"
            variant="ghost"
            class="abba-footer-action"
            :disabled="isResettingConversation || isWaitingForAI"
            @click="startNewTopic"
          >
            <Loader2
              v-if="isResettingConversation"
              class="w-4 h-4 animate-spin"
            />
            <RefreshCw
              v-else
              class="w-4 h-4"
            />
            Start New Topic
          </Button>
          <Button
            type="button"
            variant="ghost"
            class="abba-footer-action"
            @click="fileInput?.click()"
          >
            <Paperclip class="w-4 h-4" />
            Attach
          </Button>
          <div class="abba-footer-enter">
            <CornerDownLeft class="w-4 h-4" />
          </div>
        </div>
      </div>
    </template>

  </div>
</template>

<style scoped>
/* Clean slate - add styles here */
</style>
